import { Box } from '@mui/material';
import {
  ThemeProvider as RemirrorThemeProvider,
  useRemirrorContext,
} from '@remirror/react';
import { generateCustomHeadingStyles } from '../../theme-helpers';

export const EditorComponent = () => {
  const { view, getRootProps } = useRemirrorContext();
  const { key: _, ...props } = getRootProps();
  const isEditing = view.editable;

  return (
    <RemirrorThemeProvider>
      <Box
        {...props}
        className="remirror-editor-wrapper"
        sx={(theme) => ({
          '&&': {
            p: '0',

            '.remirror-editor': {
              borderRadius: theme.spacing(0),
              boxShadow: `0px 0px 0px 1px ${theme.palette.neutral[300]}`,
              margin: '0 1px 0 1px',

              '&:focus, &:active': {
                boxShadow: `0px 0px 0px 1px ${theme.palette.neutral[500]}`,
              },

              fontFamily: theme.typography.fontFamily,
              ...(isEditing && {
                backgroundColor: theme.palette.background.paper,
                maxHeight: 400,
                minHeight: 200,
              }),

              ...(!isEditing && {
                boxShadow: 'none',
                maxHeight: 'none',
                minHeight: 'auto',
                overflowY: 'visible',
                padding: '0',

                '&:focus, &:active': {
                  boxShadow: 'none',
                },
              }),

              'input[type="checkbox"]': {
                appearance: 'none',
                backgroundColor: '#fff',
                borderRadius: '0.2em',
                boxShadow: `inset 0px 0px 0px 2px ${theme.palette.neutral[700]}`,
                display: 'grid',
                height: 18,
                margin: '0',
                placeContent: 'center',
                width: 18,

                '&:checked': {
                  boxShadow: 'none',
                  backgroundColor: theme.palette.primary.dark,
                  filter: 'brightness(1)',

                  '&:before': {
                    backgroundColor: '#fff',
                    clipPath:
                      'polygon(13% 42%, 1% 57%, 35% 90%, 100% 20%, 86% 8%, 35% 63%)',
                    content: '""',
                    height: '100%',
                    position: 'absolute',
                    transform: 'scale(0.7)',
                    transformOrigin: 'center',
                    width: '100%',
                  },
                },
              },

              table: {
                backgroundColor: theme.palette.background.paper,
                borderCollapse: 'collapse',
                borderRadius: `${theme.shape.borderRadius}px`,
                borderSpacing: 0,

                '&.remirror-table-with-controllers': {
                  marginBottom: theme.spacing(6),
                  mx: 'auto',
                  width: '95%',

                  [theme.breakpoints.up('md')]: {
                    width: '96%',
                  },
                  [theme.breakpoints.up('lg')]: {
                    width: '97%',
                  },
                  [theme.breakpoints.up('xl')]: {
                    width: '98%',
                  },

                  'th, td': {
                    '&:not(.remirror-controllers-toggle)': {
                      padding: theme.spacing(1.5, 2),
                    },
                  },
                },

                '&:not(.remirror-table-with-controllers)': {
                  boxShadow: theme.shadows[1],

                  'th, td': {
                    padding: theme.spacing(2),
                    border: 0,
                    borderBottom: `1px solid ${theme.palette.neutral[300]}`,
                  },

                  'tr:last-child': {
                    'td, th': {
                      borderBottom: 0,
                    },
                  },
                },

                ...(!isEditing && {
                  mb: 2,
                }),

                'td, th': {
                  textAlign: 'left',
                },

                'th p': {
                  fontWeight: theme.typography.fontWeightBold,
                },
              },

              'ol, ul': {
                mt: 0,
                mb: 1,
                ml: 5,

                li: {
                  marginBottom: theme.spacing(1),

                  '&.remirror-list-item-with-custom-mark': {
                    '&::marker': {
                      content: '" "',
                    },
                  },

                  '& .remirror-list-item-marker-container': {
                    top: '3px',
                  },
                },
              },

              a: {
                color: theme.palette.info.main,
                textDecoration: 'underline',

                ...(!isEditing && {
                  '&:hover': {
                    textDecoration: 'none',
                  },
                }),
              },

              hr: {
                borderColor: theme.palette.neutral[100],
                outline: 'none',
              },

              p: {
                color: theme.palette.text.primary,
                ...theme.typography.body1,
              },

              '> *:not(:last-child):not(.remirror-positioner-widget):not(.remirror-table-with-controllers)':
                {
                  marginBottom: theme.spacing(3),
                },

              '.floating-link-toolbar': {
                zIndex: 1000,
              },

              mark: {
                padding: '0',
              },

              '.selection': {
                backgroundColor: (theme) => theme.palette.primary.light,
              },

              '.remirror-is-empty:first-of-type::before': {
                fontStyle: 'normal',
                fontSize: theme.typography.body2.fontSize,
                lineHeight: theme.typography.body2.lineHeight,
              },

              '.remirror-table-delete-inner-button': {
                backgroundColor: theme.palette.error.main,
              },

              '.remirror-table-delete-inner-button, .remirror-table-insert-button':
                {
                  alignItems: 'center',
                  borderRadius: '50%',
                  display: 'flex',
                  justifyContent: 'center',
                  transform: 'scale(1.3)',

                  svg: {
                    transform: 'scale(0.8)',
                  },
                },

              ...generateCustomHeadingStyles(theme),
            },
          },
        })}
      />
    </RemirrorThemeProvider>
  );
};
